/**
 * LanguageSelector Component (v2)
 * 
 * Top-right language button that opens the LanguagePickerDialog modal.
 * Saves to MongoDB + localStorage + forces page refresh.
 * Same behavior as Settings page language change.
 */

import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import { getLanguageByCode, setStoredLanguage } from '../../lib/languages';
import { Button } from '../ui/button';
import { Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import LanguagePickerDialog from '../LanguagePickerDialog';

export default function LanguageSelector({ variant = 'default', className = '' }) {
  const { language, t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const currentLangInfo = getLanguageByCode(language);

  const handleLanguageChange = async (newLanguage) => {
    try {
      // Save to server
      const response = await api.put('/auth/me', {
        preferred_language: newLanguage
      });

      // Update localStorage
      setStoredLanguage(newLanguage);

      // Update auth context
      if (updateUser) {
        updateUser(response);
      }

      // Show success
      toast.success(t('language.toast.saved') || 'Language saved!');

      // Force refresh to apply everywhere
      setTimeout(() => {
        window.location.reload();
      }, 500);

      return response;
    } catch (error) {
      toast.error('Failed to save language preference');
      throw error;
    }
  };

  const currentCode = currentLangInfo?.code?.split('-')[0]?.toUpperCase() || 'EN';

  return (
    <>
      {variant === 'icon-only' ? (
        <Button 
          variant="ghost" 
          size="icon" 
          className={className}
          onClick={() => setDialogOpen(true)}
          data-testid="language-selector-btn"
          aria-label={t('settings.profile.language') || 'Language'}
        >
          <Globe className="h-5 w-5" />
        </Button>
      ) : variant === 'compact' ? (
        <Button 
          variant="ghost" 
          size="sm" 
          className={`gap-1.5 ${className}`}
          onClick={() => setDialogOpen(true)}
          data-testid="language-selector-btn"
        >
          <Globe className="h-4 w-4" />
          <span className="uppercase text-xs font-medium">{currentCode}</span>
        </Button>
      ) : (
        <Button 
          variant="outline" 
          className={`gap-2 ${className}`}
          onClick={() => setDialogOpen(true)}
          data-testid="language-selector-btn"
        >
          <Globe className="h-4 w-4" />
          <span>{currentLangInfo?.nativeName || 'English'}</span>
        </Button>
      )}

      <LanguagePickerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentLanguage={user?.preferred_language || language}
        onLanguageChange={handleLanguageChange}
      />
    </>
  );
}
